import { useState, useEffect } from "react";
import { dashboard } from "@/lib/api";
import { Radar, FolderKanban, Users, IndianRupee, Clock, ArrowUpRight } from "lucide-react";
import { useNavigate } from "react-router-dom";

const formatCurrency = (val) => {
  if (val >= 10000000) return `${(val / 10000000).toFixed(1)} Cr`;
  if (val >= 100000) return `${(val / 100000).toFixed(1)} L`;
  if (val >= 1000) return `${(val / 1000).toFixed(1)} K`;
  return val?.toLocaleString("en-IN") || "0";
};

const statusColor = {
  New: "bg-[#2ECC71]/10 text-[#2ECC71] border-[#2ECC71]/20",
  Bidding: "bg-[#FFB800]/10 text-[#FFB800] border-[#FFB800]/20",
  Won: "bg-[#FF5C1A]/10 text-[#FF5C1A] border-[#FF5C1A]/20",
  Lost: "bg-[#E74C3C]/10 text-[#E74C3C] border-[#E74C3C]/20",
};

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    dashboard.stats().then((r) => { setStats(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="dashboard-loading">
        <div className="font-mono text-sm text-[#888880] tracking-widest uppercase">Loading dashboard...</div>
      </div>
    );
  }

  if (!stats) {
    const statCards = [
      { label: "Active Tenders", value: 0, icon: Radar, color: "#FF5C1A" },
      { label: "Active Projects", value: 0, icon: FolderKanban, color: "#FFB800" },
      { label: "Total Workers", value: 0, icon: Users, color: "#2ECC71" },
      { label: "Monthly Revenue", value: "0", icon: IndianRupee, color: "#FF5C1A" },
    ];
    return (
      <div className="space-y-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {statCards.map((s) => (
            <div key={s.label} className="bg-[#111110] border border-[#27272A] p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="p-2 rounded-sm" style={{ backgroundColor: `${s.color}15` }}>
                  <s.icon size={18} style={{ color: s.color }} />
                </div>
              </div>
              <div className="font-heading text-4xl tracking-wide" style={{ color: s.color }}>0</div>
              <div className="font-mono text-[10px] text-[#888880] tracking-[2px] uppercase mt-1">{s.label}</div>
            </div>
          ))}
        </div>
        <div className="text-center py-12 text-[#888880] font-mono text-sm">No data yet — start by adding a tender or project.</div>
      </div>
    );
  }

  const statCards = [
    { label: "Active Tenders", value: stats.active_tenders, icon: Radar, color: "#FF5C1A" },
    { label: "Active Projects", value: stats.active_projects, icon: FolderKanban, color: "#FFB800" },
    { label: "Total Workers", value: stats.total_workers, icon: Users, color: "#2ECC71" },
    { label: "Monthly Revenue", value: `${formatCurrency(stats.monthly_revenue)}`, icon: IndianRupee, color: "#FF5C1A", isCurrency: true },
  ];

  return (
    <div className="space-y-8" data-testid="dashboard">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((s, i) => (
          <div
            key={s.label}
            data-testid={`stat-card-${s.label.toLowerCase().replace(/\s/g, '-')}`}
            className="bg-[#111110] border border-[#27272A] p-6 relative overflow-hidden group hover:border-[#FF5C1A]/50 transition-colors"
            style={{ animationDelay: `${i * 80}ms` }}
          >
            <div className="flex items-start justify-between mb-4">
              <div className="p-2 rounded-sm" style={{ backgroundColor: `${s.color}15` }}>
                <s.icon size={18} style={{ color: s.color }} />
              </div>
              <ArrowUpRight size={14} className="text-[#27272A] group-hover:text-[#FF5C1A] transition-colors" />
            </div>
            <div className="font-heading text-4xl tracking-wide" style={{ color: s.color }}>
              {s.isCurrency ? "" : ""}{s.value}
            </div>
            <div className="font-mono text-[10px] text-[#888880] tracking-[2px] uppercase mt-1">{s.label}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Recent Tenders */}
        <div className="bg-[#111110] border border-[#27272A] p-6" data-testid="recent-tenders">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-heading text-xl tracking-[2px]">RECENT TENDERS</h3>
            <button
              data-testid="view-all-tenders-btn"
              onClick={() => navigate("/app/tenders")}
              className="font-mono text-[10px] text-[#FF5C1A] tracking-widest uppercase hover:text-[#FFB800] transition-colors"
            >
              View All
            </button>
          </div>
          <div className="space-y-2">
            {stats.recent_tenders?.length === 0 && (
              <div className="text-[#888880] text-sm py-4 text-center">No tenders yet</div>
            )}
            {stats.recent_tenders?.map((t) => (
              <div key={t.id} className="bg-[#0A0A08] px-4 py-3 flex items-center justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">{t.title}</div>
                  <div className="font-mono text-xs text-[#FFB800] mt-0.5">{formatCurrency(t.value)}</div>
                </div>
                <span className={`font-mono text-[10px] px-2 py-0.5 border flex-shrink-0 ${statusColor[t.status] || statusColor.New}`}>
                  {t.status?.toUpperCase()}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Upcoming Deadlines */}
        <div className="bg-[#111110] border border-[#27272A] p-6" data-testid="upcoming-deadlines">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-heading text-xl tracking-[2px]">UPCOMING DEADLINES</h3>
            <Clock size={16} className="text-[#888880]" />
          </div>
          <div className="space-y-2">
            {stats.upcoming_deadlines?.length === 0 && (
              <div className="text-[#888880] text-sm py-4 text-center">No deadlines</div>
            )}
            {stats.upcoming_deadlines?.map((d, i) => (
              <div key={i} className="bg-[#0A0A08] px-4 py-3 flex items-center justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">{d.title}</div>
                  <div className="font-mono text-[10px] text-[#888880] mt-0.5 uppercase">{d.type}</div>
                </div>
                <span className="font-mono text-xs text-[#FFB800] flex-shrink-0">{d.deadline}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
